import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isEntryReleased, isFeatureReleased, releasePathFor } from '@app/shared';
import type { ReleaseStateDto } from '@app/shared';
import { api } from '../lib/api';

/**
 * Release switches (docs/technical/release.md). The server refuses the API of unreleased modules;
 * this side greys out their menu entries and keeps their pages closed.
 */
export const releaseKeys = { state: ['release'] as const };

export function useReleaseState() {
  return useQuery({
    queryKey: releaseKeys.state,
    queryFn: () => api.get<{ data: ReleaseStateDto }>('/release').then((r) => r.data),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

/**
 * `released(path)` for a menu entry's or page's address. While the switches load, or if they cannot
 * be read, everything counts as released: the server still refuses what is closed.
 */
export function useReleased(): (path: string | undefined) => boolean {
  const { data } = useReleaseState();
  return useCallback(
    (path: string | undefined) => !data || isEntryReleased(data, path && releasePathFor(path)),
    [data],
  );
}

/**
 * `featureOn(key)` for a feature switch (RELEASE_FEATURES). As for menu entries, everything counts
 * as on while the switches load or cannot be read; the server still refuses an off feature's API.
 */
export function useFeatureReleased(): (key: string) => boolean {
  const { data } = useReleaseState();
  return useCallback((key: string) => !data || isFeatureReleased(data, key), [data]);
}
